import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plane, Plus, Search, MoreVertical, Edit, Trash2, Eye, RefreshCw,
  Building2, Shield, Wrench, AlertTriangle, BarChart3
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const AIRCRAFT_STATUS = {
  active: { label: 'Active', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  stored: { label: 'Stored', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  retired: { label: 'Retired', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  leased_out: { label: 'Leased Out', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  under_maintenance: { label: 'Under Maintenance', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  aog: { label: 'AOG', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const AIRCRAFT_CATEGORIES = [
  { value: 'aeroplane', label: 'Aeroplane' },
  { value: 'helicopter', label: 'Helicopter' },
  { value: 'powered_lift', label: 'Powered Lift' },
  { value: 'glider', label: 'Glider' },
  { value: 'uav', label: 'UAV' },
];

const OPERATIONAL_ROLES = [
  { value: 'passenger', label: 'Passenger' },
  { value: 'cargo', label: 'Cargo' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'training', label: 'Training' },
  { value: 'charter', label: 'Charter' },
  { value: 'vip', label: 'VIP' },
];

const WEIGHT_CATEGORIES = [
  { value: 'light', label: 'Light (<7,000 kg)' },
  { value: 'medium', label: 'Medium (7,000-136,000 kg)' },
  { value: 'heavy', label: 'Heavy (>136,000 kg)' },
  { value: 'super', label: 'Super (A380, An-225)' },
];

export default function AircraftPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  const [aircraft, setAircraft] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFleet, setFilterFleet] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [fleetGroups, setFleetGroups] = useState([]);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [formData, setFormData] = useState({
    registration: '',
    aircraft_type: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    category: 'aeroplane',
    icao_type_designator: '',
    year_of_manufacture: '',
    country_of_registry: '',
    status: 'active',
    operational_role: 'passenger',
    weight_category: 'medium',
    mtow_kg: '',
    fleet_group: '',
    base_airport: '',
    passenger_config: '',
    operator_id: '',
    owner_id: '',
    camo_id: '',
    maintenance_provider_id: '',
  });

  useEffect(() => {
    fetchAircraft();
    fetchStats();
    fetchOrganizations();
    fetchFleetGroups();
    fetchAircraftTypes();
  }, [api]);

  const fetchAircraft = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('aircraft_type', filterType);
      if (filterFleet !== 'all') params.append('fleet_group', filterFleet);
      
      const response = await api.get(`/aircraft?${params.toString()}`);
      setAircraft(response.data);
    } catch (error) {
      toast.error('Failed to fetch aircraft');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/aircraft/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations?limit=200');
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchFleetGroups = async () => {
    try {
      const response = await api.get('/aircraft/fleet-groups');
      setFleetGroups(response.data.fleet_groups || []);
    } catch (error) {
      console.error('Failed to fetch fleet groups:', error);
    }
  };

  const fetchAircraftTypes = async () => {
    try {
      const response = await api.get('/aircraft/types');
      setAircraftTypes(response.data.types || []);
    } catch (error) {
      console.error('Failed to fetch aircraft types:', error);
    }
  };

  const handleCreateAircraft = async () => {
    try {
      if (!formData.registration || !formData.aircraft_type || !formData.serial_number) {
        toast.error('Please fill in required fields');
        return;
      }
      
      const payload = {
        ...formData,
        year_of_manufacture: formData.year_of_manufacture ? parseInt(formData.year_of_manufacture) : null,
        mtow_kg: formData.mtow_kg ? parseFloat(formData.mtow_kg) : null,
      };
      
      if (editMode && editingId) {
        // Update existing aircraft
        await api.put(`/aircraft/${editingId}`, payload);
        toast.success('Aircraft updated successfully');
      } else {
        // Create new aircraft
        await api.post('/aircraft', payload);
        toast.success('Aircraft registered successfully');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchAircraft();
      fetchStats();
      fetchFleetGroups();
      fetchAircraftTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} aircraft`);
      console.error(error);
    }
  };

  const handleEditAircraft = async (ac) => {
    try {
      // Fetch full aircraft details
      const response = await api.get(`/aircraft/${ac.id}`);
      const data = response.data;
      
      setFormData({
        registration: data.registration || '',
        aircraft_type: data.aircraft_type || '',
        manufacturer: data.manufacturer || '',
        model: data.model || '',
        serial_number: data.serial_number || '',
        category: data.category || 'aeroplane',
        icao_type_designator: data.icao_type_designator || '',
        year_of_manufacture: data.year_of_manufacture?.toString() || '',
        country_of_registry: data.country_of_registry || '',
        status: data.status || 'active',
        operational_role: data.operational_role || 'passenger',
        weight_category: data.weight_category || 'medium',
        mtow_kg: data.mtow_kg?.toString() || '',
        fleet_group: data.fleet_group || '',
        base_airport: data.base_airport || '',
        passenger_config: data.passenger_config || '',
        operator_id: data.operator_id || '',
        owner_id: data.owner_id || '',
        camo_id: data.camo_id || '',
        maintenance_provider_id: data.maintenance_provider_id || '',
      });
      
      setEditMode(true);
      setEditingId(ac.id);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error('Failed to load aircraft details');
      console.error(error);
    }
  };

  const handleDeleteAircraft = async (aircraftId) => {
    if (!window.confirm('Are you sure you want to retire this aircraft?')) return;
    
    try {
      await api.delete(`/aircraft/${aircraftId}`);
      toast.success('Aircraft retired');
      fetchAircraft();
      fetchStats();
    } catch (error) {
      toast.error('Failed to retire aircraft');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      registration: '',
      aircraft_type: '',
      manufacturer: '',
      model: '',
      serial_number: '',
      category: 'aeroplane',
      icao_type_designator: '',
      year_of_manufacture: '',
      country_of_registry: '',
      status: 'active',
      operational_role: 'passenger',
      weight_category: 'medium',
      mtow_kg: '',
      fleet_group: '',
      base_airport: '',
      passenger_config: '',
      operator_id: '',
      owner_id: '',
      camo_id: '',
      maintenance_provider_id: '',
    });
    setEditMode(false);
    setEditingId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const viewAircraftDetails = async (ac) => {
    try {
      const response = await api.get(`/aircraft/${ac.id}`);
      setSelectedAircraft(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to fetch aircraft details');
    }
  };

  const getOrgsByType = (type) => {
    return organizations.filter(org => org.organization_types?.includes(type));
  };

  const filteredAircraft = aircraft.filter(ac => {
    if (searchQuery && 
        !ac.registration?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !ac.aircraft_type?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !ac.serial_number?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
            Aircraft Registry
          </h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
            Register and manage aircraft with accountability tracking
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-aircraft-btn">
          <Plus className="w-4 h-4" />
          Register Aircraft
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                {stats.total || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Total Aircraft
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">
                {stats.active || 0}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Active
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-sky-500">
                {Object.keys(stats.by_type || {}).length}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Aircraft Types
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-500">
                {Object.keys(stats.by_fleet_group || {}).length}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Fleet Groups
              </div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-500">
                {Object.keys(stats.by_operator || {}).length}
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Operators
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by registration, type, MSN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="aircraft-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(AIRCRAFT_STATUS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {aircraftTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterFleet} onValueChange={setFilterFleet}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Fleet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fleets</SelectItem>
                {fleetGroups.map(group => (
                  <SelectItem key={group} value={group}>{group}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAircraft} className="gap-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Aircraft Table */}
      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead className={isDark ? "text-slate-400" : ""}>Registration</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Type / Model</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>MSN</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Operator</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Fleet</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Status</TableHead>
                <TableHead className={cn("w-[50px]", isDark ? "text-slate-400" : "")}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAircraft.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                      No aircraft found
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAircraft.map(ac => (
                  <TableRow 
                    key={ac.id} 
                    className={cn("cursor-pointer", isDark ? "border-slate-800 hover:bg-slate-800/50" : "hover:bg-gray-50")}
                    onClick={() => viewAircraftDetails(ac)}
                    data-testid={`aircraft-row-${ac.registration}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Plane className={cn("w-4 h-4", isDark ? "text-sky-400" : "text-sky-600")} />
                        <div>
                          <div className={cn("font-mono font-bold", isDark ? "text-white" : "text-gray-900")}>
                            {ac.registration}
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>
                            {ac.aircraft_number}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={isDark ? "text-white" : "text-gray-900"}>
                        {ac.aircraft_type}
                      </div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        {ac.manufacturer} {ac.model}
                      </div>
                    </TableCell>
                    <TableCell className={cn("font-mono", isDark ? "text-slate-300" : "text-gray-700")}>
                      {ac.serial_number}
                    </TableCell>
                    <TableCell>
                      <div className={isDark ? "text-white" : "text-gray-900"}>
                        {ac.operator_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {ac.fleet_group ? (
                        <Badge variant="outline" className="text-xs">
                          {ac.fleet_group}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={AIRCRAFT_STATUS[ac.status]?.color || AIRCRAFT_STATUS.active.color}>
                        {AIRCRAFT_STATUS[ac.status]?.label || ac.status}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewAircraftDetails(ac)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditAircraft(ac)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Aircraft
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Safety Metrics
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            View Incidents
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-500"
                            onClick={() => handleDeleteAircraft(ac.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Retire Aircraft
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Aircraft Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className={cn("max-w-3xl max-h-[90vh] overflow-y-auto", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader>
            <DialogTitle className={isDark ? "text-white" : ""}>
              {editMode ? 'Edit Aircraft' : 'Register Aircraft'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Identification</TabsTrigger>
              <TabsTrigger value="operational">Operational</TabsTrigger>
              <TabsTrigger value="accountability">Accountability</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Registration *</Label>
                  <Input
                    value={formData.registration}
                    onChange={(e) => setFormData(prev => ({ ...prev, registration: e.target.value.toUpperCase() }))}
                    placeholder="HZ-AKA"
                    className="font-mono"
                    data-testid="aircraft-registration"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Aircraft Type *</Label>
                  <Input
                    value={formData.aircraft_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, aircraft_type: e.target.value }))}
                    placeholder="A320-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>MSN (Serial) *</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                    placeholder="2450"
                    className="font-mono"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Manufacturer</Label>
                  <Select 
                    value={formData.manufacturer} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, manufacturer: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select manufacturer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Airbus">Airbus</SelectItem>
                      <SelectItem value="Boeing">Boeing</SelectItem>
                      <SelectItem value="Embraer">Embraer</SelectItem>
                      <SelectItem value="ATR">ATR</SelectItem>
                      <SelectItem value="Bombardier">Bombardier</SelectItem>
                      <SelectItem value="Cessna">Cessna</SelectItem>
                      <SelectItem value="Gulfstream">Gulfstream</SelectItem>
                      <SelectItem value="Dassault">Dassault</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Model</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                    placeholder="A320-214"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>ICAO Type</Label>
                  <Input
                    value={formData.icao_type_designator}
                    onChange={(e) => setFormData(prev => ({ ...prev, icao_type_designator: e.target.value.toUpperCase() }))}
                    placeholder="A320"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Year of Manufacture</Label>
                  <Input
                    type="number"
                    value={formData.year_of_manufacture}
                    onChange={(e) => setFormData(prev => ({ ...prev, year_of_manufacture: e.target.value }))}
                    placeholder="2015"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Country of Registry</Label>
                  <Input
                    value={formData.country_of_registry}
                    onChange={(e) => setFormData(prev => ({ ...prev, country_of_registry: e.target.value.toUpperCase() }))}
                    placeholder="SA"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {AIRCRAFT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            
            <TabsContent value="operational" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AIRCRAFT_STATUS).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Operational Role</Label>
                  <Select 
                    value={formData.operational_role} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, operational_role: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {OPERATIONAL_ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Weight Category</Label>
                  <Select 
                    value={formData.weight_category} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, weight_category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select weight category" />
                    </SelectTrigger>
                    <SelectContent>
                      {WEIGHT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>MTOW (kg)</Label>
                  <Input
                    type="number"
                    value={formData.mtow_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, mtow_kg: e.target.value }))}
                    placeholder="78000"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Fleet Group</Label>
                  <Input
                    value={formData.fleet_group}
                    onChange={(e) => setFormData(prev => ({ ...prev, fleet_group: e.target.value }))}
                    placeholder="A320 Fleet"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Base Airport (ICAO)</Label>
                  <Input
                    value={formData.base_airport}
                    onChange={(e) => setFormData(prev => ({ ...prev, base_airport: e.target.value.toUpperCase() }))}
                    placeholder="OERK"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Passenger Config</Label>
                  <Input
                    value={formData.passenger_config}
                    onChange={(e) => setFormData(prev => ({ ...prev, passenger_config: e.target.value }))}
                    placeholder="Y180 or C12Y168"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="accountability" className="space-y-4 mt-4">
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                Link this aircraft to responsible organizations for accountability tracking.
              </p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <Plane className="w-4 h-4" /> Operator (AOC Holder)
                  </Label>
                  <Select 
                    value={formData.operator_id || "none"} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, operator_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select operator" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {getOrgsByType('operator').map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <Building2 className="w-4 h-4" /> Owner
                  </Label>
                  <Select 
                    value={formData.owner_id || "none"} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, owner_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <Shield className="w-4 h-4" /> CAMO
                  </Label>
                  <Select 
                    value={formData.camo_id || "none"} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, camo_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select CAMO" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {getOrgsByType('camo').map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "")}>
                    <Wrench className="w-4 h-4" /> Maintenance Provider (MRO)
                  </Label>
                  <Select 
                    value={formData.maintenance_provider_id || "none"} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, maintenance_provider_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select MRO" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {getOrgsByType('mro').map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateAircraft} data-testid="save-aircraft-btn">
              {editMode ? 'Update Aircraft' : 'Register Aircraft'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aircraft Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "")}>
              <Plane className="w-5 h-5 text-sky-500" />
              {selectedAircraft?.registration}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAircraft && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={AIRCRAFT_STATUS[selectedAircraft.status]?.color}>
                  {AIRCRAFT_STATUS[selectedAircraft.status]?.label}
                </Badge>
                {selectedAircraft.fleet_group && (
                  <Badge variant="outline">{selectedAircraft.fleet_group}</Badge>
                )}
                <Badge variant="outline">{selectedAircraft.operational_role}</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Aircraft Type</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedAircraft.aircraft_type}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>MSN</Label>
                  <p className={cn("font-mono", isDark ? "text-white" : "")}>{selectedAircraft.serial_number}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Manufacturer / Model</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedAircraft.manufacturer} {selectedAircraft.model}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Year</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedAircraft.year_of_manufacture || '-'}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Base Airport</Label>
                  <p className={cn("font-mono", isDark ? "text-white" : "")}>{selectedAircraft.base_airport || '-'}</p>
                </div>
                <div>
                  <Label className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Configuration</Label>
                  <p className={isDark ? "text-white" : ""}>{selectedAircraft.passenger_config || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <Label className={cn("text-xs font-semibold", isDark ? "text-slate-400" : "text-gray-500")}>
                  ACCOUNTABILITY
                </Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className={cn("p-3 rounded", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-sky-500" />
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Operator</span>
                    </div>
                    <p className={cn("font-medium mt-1", isDark ? "text-white" : "")}>
                      {selectedAircraft.operator_name || 'Not assigned'}
                    </p>
                  </div>
                  <div className={cn("p-3 rounded", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-purple-500" />
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Owner</span>
                    </div>
                    <p className={cn("font-medium mt-1", isDark ? "text-white" : "")}>
                      {selectedAircraft.owner_name || 'Not assigned'}
                    </p>
                  </div>
                  <div className={cn("p-3 rounded", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-500" />
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>CAMO</span>
                    </div>
                    <p className={cn("font-medium mt-1", isDark ? "text-white" : "")}>
                      {selectedAircraft.camo_name || 'Not assigned'}
                    </p>
                  </div>
                  <div className={cn("p-3 rounded", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-orange-500" />
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>MRO</span>
                    </div>
                    <p className={cn("font-medium mt-1", isDark ? "text-white" : "")}>
                      {selectedAircraft.maintenance_provider_name || 'Not assigned'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
